import {
  Breadcrumb,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from "antd";
import "antd/dist/antd.min.css";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../axios";
import ButtonField from "../../common/htmlComponents/ButtonField";
import { ReactComponent as ArchiveRestore } from "../../image/ArchiveRestore.svg";
import SearchIcon from "../../image/SearchIcon.svg";
import "../../styles/Main.scss";
import { authUserData } from "../../utils/Helper";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";

const { Search } = Input;

const ArchivedCompanyDocument = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [archiveCompanyDocument, setArchiveCompanyDocument] = useState([]);
  const CompanyId = authUserData()?.company ? authUserData()?.company : null;
  const [totalCount, settotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [reload, setReload] = useState(false);
  const hasPermission = authUserData()?.role[0].name;
  const navigate = useNavigate();

  // state initialisation
  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    search: "",
    total: 0,
  });
  useEffect(() => {
    // Function to fetch the archived company documents
    const getCompanyOnboardingArchiveList = async () => {
      setLoading(true); // Set loading state to true while data is being fetched

      try {
        // Making a GET request to fetch archived documents based on company ID and state
        let apiResponse = await getRequest(
          `company-attachment-restore/?company_id=${CompanyId}`,
          state
        );

        if (apiResponse.data.results.success) {
          // If the API call is successful, update the state with the received data
          setArchiveCompanyDocument(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count); // Update the total count
        } else {
          // If the API call fails, show an error notification
          ErrorNotificationMsg(t("error_in_archivelist"));
        }
        setLoading(false); // Set loading state to false after data fetching
      } catch (error) {
        setLoading(false); // Set loading state to false if an error occurs
      }
    };

    getCompanyOnboardingArchiveList(); // Invoke the function to fetch the data
  }, [state, t, reload]); // Dependencies for useEffect to trigger re-fetching of data

  // Function to handle table changes such as pagination, filtering, and sorting
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order); // Get sorting value based on column and order
    setState({
      ...state,
      page: pagination.current, // Update page number
      pagesize: pagination.pageSize, // Update page size
      ordering: orderBy, // Update ordering value
    });
  };

  // Function to map sorting values based on the column key and sort order
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id"; // Default sorting by ID

    // Check sortField and sortOrder to set the corresponding sorting value
    if (sortField === "attachment_name" && sortOrder === "ascend") {
      sortingEnumKey = "attachment_name";
    }
    if (sortField === "attachment_name" && sortOrder === "descend") {
      sortingEnumKey = "-attachment_name";
    }
    if (sortField === "attachment_file" && sortOrder === "ascend") {
      sortingEnumKey = "attachment_file";
    }
    if (sortField === "attachment_file" && sortOrder === "descend") {
      sortingEnumKey = "-attachment_file";
    }

    return sortingEnumKey; // Return the sorting value
  };

  // Function to handle delayed search input
  const searchCallbackDelayed = (event) => {
    const { value } = event.target; // Get the search value from input
    debounceLoadData(value, onSearch); // Debounce the search to avoid too many API calls
  };

  // Function to handle search input and update the state
  const onSearch = (value) => {
    setState({
      ...state,
      search: value, // Update the search value in the state
    });
  };

  // Debounce function to delay the search API call
  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value); // Call the search function with the value after delay
  }, 1000);

  // Row selection configuration for the table
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRow(selectedRowKeys); // Update selected rows state when selection changes
    },
  };

  // Function to handle restoring documents
  const handleRestore = async (id = "") => {
    let ids = selectedRow; // Get selected row IDs
    if (ids.length === 0) {
      ids.push(id); // If no row is selected, add the given ID to the list
    }
    try {
      // Making a POST request to restore the documents
      let apiResponse = await postRequest("company-attachment-restore/", {
        deleted: ids, // Send the list of IDs to be restored
      });

      if (apiResponse.data.success) {
        // If the API call is successful, show a success notification
        SuccessNotificationMsg(t("document_restore_successfully"));
        setSelectedRow([]); // Clear selected rows
        setReload(!reload); // Trigger reload to refresh the list
      } else {
        // If the API call fails, show an error notification
        ErrorNotificationMsg(t("error_in_restore_document"));
      }
    } catch (error) {
      ErrorNotificationMsg(error); // Show error notification if an error occurs
    }
  };


  const columns = [
    {
      title: "Title",
      dataIndex: "attachment_name",
      key: "attachment_name",
      sorter: true,
    },
    {
      title: "Document",
      dataIndex: "attachment_file",
      key: "attachment_file",
      sorter: true,
      render: (attachment_file) =>
        attachment_file && (
          <>
            <a
              style={{ textDecoration: "none", color: "black" }}
              href={`${attachment_file}`}
              download
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={attachment_file}
                style={{ width: "50px", height: "50px" }}
                alt=""
              />
            </a>
          </>
        ),
    },

    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (text, record) => (
        <Space>
          <Popconfirm
            className="action"
            title={t("pop_restore_title")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            onConfirm={() => handleRestore(record.id)}
          >
            <ArchiveRestore
              className="ArchiverestorImg"
              style={{ cursor: "pointer" }}
            />{" "}
          </Popconfirm>
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip
              color={colors}
              placement="right"
              title={
                <span style={{ color: "#fff" }}>
                  View the archived documents related to company transactions
                  and procurement history.
                </span>
              }
            >
              <h3>{t("archived_company_document")}</h3>
            </Tooltip>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              {hasPermission === "Company Admin" && (
                <Breadcrumb.Item>
                  <Link to="/my-company">My Company</Link>
                </Breadcrumb.Item>
              )}
              <Breadcrumb.Item>{t("archive")} </Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_document")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restore"
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5">
        <div className="PR-body">
          <Row className="tblheader">
          </Row>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={archiveCompanyDocument}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
              locale: {
                items_per_page: "Items / Page", // Text for the size changer
              },
            }}
            onChange={handleTableChange}
          />
          <div className="FormFooter Container">
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              htmlType="button"
              label={t("pl_back_to_list")}
              onClick={() => navigate(`/my-company`)}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default ArchivedCompanyDocument;
